import { PlusIcon, XMarkIcon } from "@heroicons/react/24/solid";
import {
    Card,
    CardBody,
    CardHeader,
    Chip,
    Dialog,
    DialogBody,
    DialogHeader,
    DialogFooter,
    IconButton,
    Tooltip,
    Typography,
    Button
} from "@material-tailwind/react";
import React, { useEffect, useState, useMemo } from 'react';
import { ClockLoader } from 'react-spinners';
import { useUserContext } from '../../context';
import { useApi } from "../../hooks/useApi";


const TABLE_HEAD_USER = ["Name", "Hotel(s)", "Status"];

function Managers() {

    const [managers, setManagers] = useState([]);
    const { assignments, fetchAccessToken } = useUserContext();
    const { getManagers, addUser, assignManager, unassignManager } = useApi();
    const [isLoading, setIsLoading] = useState(true);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [newManager, setNewManager] = useState({
        firstName: 'Manager',
        lastName: '',
        email: '',
        mobile: ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [showAssignDialog, setShowAssignDialog] = useState(false);
    const [selectedManager, setSelectedManager] = useState(null);
    const [selectedHotelToAssign, setSelectedHotelToAssign] = useState('');

    const hotelDetails = useMemo(() => {
        if (!assignments?.hotels) {
            return new Map();
        }
        return assignments.hotels.reduce((acc, hotel) => {
            acc.set(hotel.id, hotel.name);
            return acc;
        }, new Map());
    }, [assignments?.hotels]);

    const hotelIds = useMemo(() => {
        return Array.from(hotelDetails.keys());
    }, [hotelDetails]);

    useEffect(() => {
        const getManagersAsync = async () => {
            try {
                setIsLoading(true);
                const token = await fetchAccessToken();
                let data = await getManagers(token);
                if (data) {
                    setManagers(data);
                }
            } catch (error) {
                console.error('Error fetching managers:', error.message);
            } finally {
                setIsLoading(false);
            }
        };
        if (hotelIds.length > 0) {
            getManagersAsync();
        }

    }, [hotelIds]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewManager(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveManager = async () => {
        if (!newManager.firstName || !newManager.lastName || !newManager.email || !newManager.mobile) {
            alert('Please fill all fields');
            return;
        }

        if (hotelIds.length === 0) {
            alert('No hotels available');
            return;
        }

        setIsSaving(true);
        try {
            setIsLoading(true);
            const token = await fetchAccessToken();
            const employee = {
                id: newManager.email.toUpperCase(),
                name: newManager.firstName + " " + newManager.lastName,
                email: newManager.email.toLowerCase(),
                mobile: newManager.mobile,
                roles: ['MANAGER'],
                hotels: []
            };

            await addUser(employee, token);

            setNewManager({
                firstName: 'Manager',
                lastName: '',
                email: '',
                mobile: ''
            });
            setShowAddDialog(false);

            const data = await getManagers(token);
            if (data) {
                setManagers(data);
            }

            alert('Manager added successfully!');
        } catch (error) {
            console.error('Error saving manager:', error);
            alert('Error saving manager. Please try again.');
        } finally {
            setIsSaving(false);
            setIsLoading(false);
        }
    };

    const handleUnassignHotel = async (managerEmail, hotelId, hotelName) => {
        const confirmed = window.confirm(`Are you sure you want to unassign "${hotelName}" from this manager?`);
        if (!confirmed) return;

        try {
            setIsLoading(false);
            const token = await fetchAccessToken();
            await unassignManager(hotelId, managerEmail, token);
            alert(`Hotel "${hotelName}" unassigned successfully!`);

            const data = await getManagers(token);
            if (data) {
                setManagers(data);
            }
        } catch (error) {
            console.error('Error unassigning hotel:', error);
            alert('Error unassigning hotel. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAssignHotel = async () => {
        if (!selectedHotelToAssign) {
            alert('Please select a hotel to assign');
            return;
        }

        try {
            setIsLoading(true);
            const token = await fetchAccessToken();
            assignManager(selectedHotelToAssign, selectedManager.email, token);
            alert(`Hotel assigned successfully!`);
            const data = await getManagers(token);
            if (data) {
                setManagers(data);
            }

            setShowAssignDialog(false);
            setSelectedManager(null);
            setSelectedHotelToAssign('');
        } catch (error) {
            alert('Error assigning hotel. Please try again.');
        }
        finally {
            setIsLoading(false);
        }
    };

    const getAvailableHotels = (managerHotels) => {
        return Array.from(hotelDetails.entries()).filter(([hotelId]) =>
            !managerHotels.includes(hotelId)
        );
    };





    return (
        <div>
            <Card className="h-full w-full">
                <CardHeader variant="gradient" color="gray" className="mt-6 mb-8 p-6 flex flex-row justify-between">
                    <Typography variant="h6" color="white">
                        Managers
                    </Typography>
                    <button className="shadow bg-blue-500 hover:bg-blue-700 focus:shadow-outline focus:outline-none text-white font-bold text-xs py-2 px-4 rounded" type="button"
                        onClick={() => setShowAddDialog(true)}>
                        Add Manager
                    </button>
                </CardHeader>
                <CardBody className="overflow-scroll px-0 pt-0 pb-2">
                    <table className="mt-0 w-full min-w-max table-auto text-left">
                        <thead>
                            <tr>
                                {TABLE_HEAD_USER.map((head) => (
                                    <th
                                        key={head}
                                        className="border-y border-blue-gray-100 bg-blue-gray-50/50 p-4"
                                    >
                                        <Typography
                                            variant="small"
                                            color="blue-gray"
                                            className="font-normal leading-none opacity-90"
                                        >
                                            {head}
                                        </Typography>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {managers.map(
                                ({ name, email, hotels, mobile }, index) => {
                                    hotels = hotels || [];
                                    const isLast = index === managers.length - 1;
                                    const classes = isLast
                                        ? "p-4"
                                        : "p-4 border-b border-blue-gray-50";

                                    return (
                                        <tr key={email} className="border-b border-blue-gray-500">
                                            <td className={classes}>
                                                <div>
                                                    <Typography
                                                        variant="small"
                                                        color="blue-gray"
                                                        className="font-semibold"
                                                    >
                                                        {name}
                                                    </Typography>
                                                    <Typography className="text-xs font-normal text-blue-gray-500">
                                                        {email}
                                                    </Typography>
                                                    <Typography className="text-xs font-normal text-blue-gray-500">
                                                        {mobile}
                                                    </Typography>
                                                </div>
                                            </td>
                                            <td className={classes}>
                                                <div className="flex flex-col gap-2">
                                                    {hotels.length > 0 ? (
                                                        hotels.map((hotelId) => (
                                                            <div key={hotelId} className="flex items-center justify-between bg-blue-50 rounded p-2">
                                                                <Typography
                                                                    variant="small"
                                                                    color="blue-gray"
                                                                    className="font-normal flex-1"
                                                                >
                                                                    {hotelDetails.get(hotelId)}
                                                                </Typography>
                                                                <Tooltip content="Unassign Hotel">
                                                                    <IconButton
                                                                        variant="text"
                                                                        color="red"
                                                                        size="sm"
                                                                        onClick={() => handleUnassignHotel(email, hotelId, hotelDetails.get(hotelId))}
                                                                    >
                                                                        <XMarkIcon className="h-6 w-6" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <Typography
                                                            variant="small"
                                                            color="blue-gray"
                                                            className="font-normal italic text-gray-500"
                                                        >
                                                            No hotels assigned
                                                        </Typography>
                                                    )}
                                                    <Tooltip content="Assign Hotel">
                                                        <Button
                                                            variant="outlined"
                                                            color="blue"
                                                            size="sm"
                                                            className="flex items-center gap-2 mt-2"
                                                            onClick={() => {
                                                                setSelectedManager({ email, name, hotels });
                                                                setShowAssignDialog(true);
                                                            }}
                                                        >
                                                            <PlusIcon className="h-6 w-6" />
                                                            Assign Hotel
                                                        </Button>
                                                    </Tooltip>
                                                </div>
                                            </td>
                                            <td className={classes}>
                                                <Chip
                                                    variant="gradient"
                                                    color={hotels.length > 0 ? "green" : "blue-gray"}
                                                    value={hotels.length > 0 ? "active" : "inactive"}
                                                    className="py-0.5 px-2 text-[11px] font-medium w-fit"
                                                />
                                            </td>
                                        </tr>
                                    );
                                },
                            )}
                        </tbody>
                    </table>
                </CardBody>
            </Card>

            <Dialog open={showAddDialog} handler={() => setShowAddDialog(false)} size="md">
                <DialogHeader>
                    <Typography variant="h5" color="blue-gray">
                        Add New Manager
                    </Typography>
                </DialogHeader>
                <DialogBody divider className="space-y-4">
                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                            First Name *
                        </Typography>
                        <input
                            type="text"
                            name="firstName"
                            value={newManager.firstName}
                            onChange={handleInputChange}
                            className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                            placeholder="Enter first name" disabled={true}
                        />
                    </div>
                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                            Last Name *
                        </Typography>
                        <input
                            type="text"
                            name="lastName"
                            value={newManager.lastName}
                            onChange={handleInputChange}
                            className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                            placeholder="Enter last name"
                        />
                    </div>
                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                            Email *
                        </Typography>
                        <input
                            type="email"
                            name="email"
                            value={newManager.email}
                            onChange={handleInputChange}
                            className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                            placeholder="Enter email address"
                        />
                    </div>
                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                            Mobile Number *
                        </Typography>
                        <input
                            type="tel"
                            name="mobile"
                            value={newManager.mobile}
                            onChange={handleInputChange}
                            className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                            placeholder="Enter mobile number"
                        />
                    </div>
                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                            Default Password
                        </Typography>
                        <input
                            type="text"
                            value={
                                newManager.mobile && newManager.mobile.length >= 4
                                    ? `Mana$${newManager.mobile.slice(-4)}`
                                    : ''
                            }
                            className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-gray-100 rounded text-sm shadow focus:outline-none w-full ease-linear transition-all duration-150"
                            readOnly
                            disabled
                        />
                        <Typography variant="extra-small" color="gray" className="mt-1">
                            The default password will be: Mana$ + last 4 digits of the mobile number
                        </Typography>
                    </div>
                </DialogBody>
                <DialogFooter className="space-x-2">
                    <Button
                        variant="text"
                        color="red"
                        onClick={() => setShowAddDialog(false)}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="gradient"
                        color="green"
                        onClick={handleSaveManager}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save Manager'}
                    </Button>
                </DialogFooter>
            </Dialog>

            <Dialog open={showAssignDialog} handler={() => setShowAssignDialog(false)} size="md">
                <DialogHeader>
                    <Typography variant="h5" color="blue-gray">
                        Assign Hotel to {selectedManager?.name}
                    </Typography>
                </DialogHeader>
                <DialogBody divider className="space-y-4">
                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                            Select Hotel to Assign *
                        </Typography>
                        <select
                            value={selectedHotelToAssign}
                            onChange={(e) => setSelectedHotelToAssign(e.target.value)}
                            className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                        >
                            <option value="">Select a hotel...</option>
                            {selectedManager && getAvailableHotels(selectedManager.hotels).map(([hotelId, hotelName]) => (
                                <option key={hotelId} value={hotelId}>
                                    {hotelName}
                                </option>
                            ))}
                        </select>
                        {selectedManager && getAvailableHotels(selectedManager.hotels).length === 0 && (
                            <Typography variant="small" color="red" className="mt-2">
                                All available hotels are already assigned to this manager.
                            </Typography>
                        )}
                    </div>
                </DialogBody>
                <DialogFooter className="space-x-2">
                    <Button
                        variant="text"
                        color="red"
                        onClick={() => {
                            setShowAssignDialog(false);
                            setSelectedManager(null);
                            setSelectedHotelToAssign('');
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="gradient"
                        color="green"
                        onClick={handleAssignHotel}
                        disabled={!selectedHotelToAssign || (selectedManager && getAvailableHotels(selectedManager.hotels).length === 0)}
                    >
                        Assign Hotel
                    </Button>
                </DialogFooter>
            </Dialog>

            <Dialog open={isLoading} className="bg-transparent shadow-none" size="md">
                <DialogBody className="bg-transparent shadow-none flex justify-center">
                    <ClockLoader color="#2be60d" />
                </DialogBody>
            </Dialog>

        </div>
    )
}

export default React.memo(Managers);
